import * as TabsPrimitive from "@radix-ui/react-tabs";
import { TabTrigger_ } from "./TabTrigger";
import { TabOverflowMenu_ } from "./TabOverflowMenu";
import { useTabOverflow_ } from "./useTabOverflow";
import { ControlBarProvider } from "./ControlBarContext";
import type { TabCountColor, TabWarning } from "../Tab";

interface TabStripMeta {
  id: string;
  title: string;
  testId?: string | undefined;
  count?: number | undefined;
  countColor?: TabCountColor | undefined;
  disabled?: boolean | undefined;
  icon?: React.ReactNode;
  warning?: TabWarning | undefined;
}

/**
 * Reorders tabs so the active tab is always in the visible set.
 * If the active tab would overflow, it replaces the last visible slot.
 */
function splitTabs<T extends { id: string }>(
  tabs: T[],
  activeId: string,
  visibleCount: number,
): { visible: T[]; overflow: T[] } {
  if (visibleCount >= tabs.length) {
    return { visible: tabs, overflow: [] };
  }

  const activeIndex = tabs.findIndex((t) => t.id === activeId);
  const visible = tabs.slice(0, visibleCount);
  const overflow = tabs.slice(visibleCount);

  // If the active tab is in the overflow set, swap it with the last visible tab
  if (activeIndex >= visibleCount) {
    const lastVisibleIndex = visibleCount - 1;
    const activeTab = tabs[activeIndex]!;
    const displaced = visible[lastVisibleIndex]!;
    visible[lastVisibleIndex] = activeTab;
    overflow.splice(overflow.indexOf(activeTab), 1);
    overflow.unshift(displaced);
  }

  return { visible, overflow };
}

interface TabStripProps {
  tabs: TabStripMeta[];
  activeTab: string;
  onTabChange: (id: string) => void;
  dense?: boolean | undefined;
  /** Controls pinned to the right end of the strip row. See Tabs' `controls`. */
  controls?: React.ReactNode;
}

/**
 * Renders the tab trigger strip with overflow detection.
 * Includes a hidden measurement layer, visible tabs, and an overflow menu.
 */
export function TabStrip_({
  tabs,
  activeTab,
  onTabChange,
  dense,
  controls,
}: TabStripProps) {
  const activeIndex = tabs.findIndex((t) => t.id === activeTab);

  // The underline strip uses gap-6 (24px). The overflow calc must use the SAME
  // gap or its width estimate is off and the strip won't collapse — keep this
  // in lockstep with `measureGap` and `listClass` below.
  const gapPx = 24;

  const { containerRef, measureRef, visibleCount } = useTabOverflow_({
    tabCount: tabs.length,
    activeIndex,
    gapPx,
  });

  const { visible, overflow } = splitTabs(tabs, activeTab, visibleCount);

  // With `controls`, the bottom border moves to a wrapper so it still spans the
  // full row, and the list becomes a shrinkable flex child. That is what keeps
  // overflow detection honest: the hook measures the LIST's width, so once the
  // controls take their share the tabs collapse into the kebab at the right
  // point. Callers without controls keep the original markup untouched — this
  // is a slot, not a rewrite.
  const listClass = controls
    ? "relative mb-0 flex min-w-0 flex-1 items-stretch gap-6"
    : "relative mb-0 flex w-full items-stretch gap-6 border-b border-border";
  // The hidden measure row must use the same inter-tab gap as the visible row
  // so overflow width detection stays accurate.
  const measureGap = "gap-6";

  return (
    <TabsPrimitive.Root
      value={activeTab}
      onValueChange={onTabChange}
      activationMode="manual"
      {...(controls
        ? { className: "flex items-end gap-4 border-b border-border" }
        : {})}
    >
      <TabsPrimitive.List ref={containerRef} className={listClass}>
        {/* Visible tabs */}
        {visible.map((meta) => (
          <TabTrigger_
            key={meta.id}
            id={meta.id}
            label={meta.title}
            testId={meta.testId}
            count={meta.count}
            countColor={meta.countColor}
            disabled={meta.disabled}
            icon={meta.icon}
            warning={meta.warning}
            isActive={activeTab === meta.id}
            dense={dense}
          />
        ))}
        <TabOverflowMenu_
          tabs={overflow}
          activeTab={activeTab}
          onSelect={onTabChange}
          dense={dense}
        />
        {/* Hidden measurement layer. It renders every tab at natural width so
            we can measure them, but that full width must NOT extend the page's
            scroll width (a `fixed`/`absolute` row wider than the viewport does).
            So it's an absolute row clipped by a zero-impact overflow-hidden
            wrapper: children keep their real offsetWidth, but nothing leaks out
            to widen the document.

            It renders LAST, after the tabs it is a copy of. Being absolute, its
            position here costs nothing visually — but a query that matches on
            tab text (`cy.contains`, `getByText`) takes the first node in
            document order, and when this row came first every such query got
            the invisible copy and any `should("be.visible")` failed. */}
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden
        >
          <div
            ref={measureRef}
            className={`absolute top-0 left-0 flex ${measureGap} opacity-0`}
          >
            {tabs.map((meta) => (
              <TabTrigger_
                key={`measure-${meta.id}`}
                id={`measure-${meta.id}`}
                label={meta.title}
                count={meta.count}
                countColor={meta.countColor}
                disabled={meta.disabled}
                icon={meta.icon}
                warning={meta.warning}
                isActive={false}
                dense={dense}
              />
            ))}
          </div>
        </div>
      </TabsPrimitive.List>
      {/* The same context Page / Panel / Tab control bars provide, so a control
          dropped in here tightens itself the way it does in those bars — a
          TextInput (and so SearchInput, which delegates to one) comes down to
          h-8 instead of towering over the tab labels. */}
      {controls && (
        <ControlBarProvider value="filters">
          <div className="shrink-0 pb-1.5">{controls}</div>
        </ControlBarProvider>
      )}
    </TabsPrimitive.Root>
  );
}
